import { FieldWrapper } from "@/components/form/FieldWrapper/FieldWrapper";
import { formatDateForMessage } from "@/utilities/date-helpers";
import { CalendarDate, toCalendarDate } from "@internationalized/date";

import ArrowButton from "@/images/icons/arrow-button.svg";
import IconCalendar from "@/images/icons/calendar.svg";
import clsx from "clsx";
import { isToday } from "date-fns";
import { useId } from "react";
import {
	Button,
	Calendar,
	CalendarCell,
	CalendarGrid,
	CalendarGridBody,
	CalendarGridHeader,
	CalendarHeaderCell,
	DateInput,
	DatePicker,
	DateSegment,
	DateValue,
	Dialog,
	Group,
	Heading,
	I18nProvider,
	Label,
	Popover,
} from "react-aria-components";
import { Control, Controller, FieldPath, FieldValues, UseControllerProps } from "react-hook-form";
import styles from "./DateField.module.scss";

export interface DateFieldProps<T extends FieldValues, TPath extends FieldPath<T>> {
	control: Control<T>;
	name: TPath;
	label: string;
	hideLabel?: boolean;
	className?: string;
	registerOptions?: UseControllerProps<T, TPath>["rules"];
	slotAfterInput?: React.ReactNode;
	isDateUnavailable?: (date: DateValue) => boolean;
	ariaDescribedBy?: string;
	minValue?: DateValue;
	minValueErrorMessage?: string;
	maxValue?: DateValue;
}

export const DateField = <T extends FieldValues, TPath extends FieldPath<T>>({
	control,
	name,
	label,
	hideLabel,
	className,
	registerOptions,
	slotAfterInput,
	isDateUnavailable,
	ariaDescribedBy,
	minValue,
	minValueErrorMessage,
	maxValue,
}: DateFieldProps<T, TPath>) => {
	const effectiveMinValue = minValue ?? (new CalendarDate(1900, 1, 1) as unknown as DateValue);
	const id = useId();
	const errorId = id + "-error";

	const minCalendarDate = toCalendarDate(effectiveMinValue);
	// A floor inside the current year is the common case, so compare the whole
	// date rather than just the year.
	const defaultMinValueErrorMessage =
		minCalendarDate.year > 1900
			? `Please enter a date on or after ${formatDateForMessage(minCalendarDate)}`
			: `Please enter a date after ${minCalendarDate.year}`;
	const effectiveRegisterOptions = {
		...registerOptions,
		validate: {
			minDate: (date: DateValue | undefined) =>
				!date || toCalendarDate(date).compare(minCalendarDate) >= 0
					? true
					: minValueErrorMessage || defaultMinValueErrorMessage,
			...registerOptions?.validate,
			...(isDateUnavailable && {
				isUnavailable: (date: DateValue | undefined) =>
					date && isDateUnavailable(date) ? "Please select an available date" : true,
			}),
		},
	};

	return (
		<I18nProvider locale="en-AU">
			<Controller
				control={control}
				name={name}
				rules={effectiveRegisterOptions}
				render={({ field, fieldState }) => {
					return (
						<DatePicker
							className={styles["date-field"]}
							aria-describedby={ariaDescribedBy}
							value={field.value}
							onChange={field.onChange}
							isDateUnavailable={isDateUnavailable}
							shouldForceLeadingZeros
							isInvalid={fieldState.invalid}
							isDisabled={field.disabled}
							minValue={effectiveMinValue}
							maxValue={maxValue}
						>
							<FieldWrapper
								{...{
									name,
									labelSlot: <Label className={styles["date-field__label"]}>{label}</Label>,
									required: registerOptions?.required as boolean,
									className,
									errorId,
								}}
							>
								<Group
									className={styles["date-field__input"]}
									id={id}
								>
									<DateInput
										ref={field.ref}
										className={styles["date-field__input-inner"]}
									>
										{segment => (
											<DateSegment
												className={styles["date-field__segment"]}
												segment={segment}
											/>
										)}
									</DateInput>
									<Button className={styles["date-field__button"]}>
										<IconCalendar />
									</Button>
								</Group>
								<Popover
									isNonModal
									className={styles["popover"]}
								>
									<Dialog>
										<Calendar
											className={styles["calendar"]}
											isDateUnavailable={isDateUnavailable}
											minValue={effectiveMinValue}
											maxValue={maxValue}
										>
											<div className={styles["calendar__header"]}>
												<Button
													slot="previous"
													className={clsx(
														styles["calendar__button"],
														styles["calendar__button--previous"],
													)}
												>
													<ArrowButton />
												</Button>
												<Heading className={styles["calendar__heading"]} />
												<Button
													slot="next"
													className={clsx(
														styles["calendar__button"],
														styles["calendar__button--next"],
													)}
												>
													<ArrowButton />
												</Button>
											</div>
											<CalendarGrid className={styles["calendar__grid"]}>
												<CalendarGridHeader>
													{day => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
												</CalendarGridHeader>
												<CalendarGridBody>
													{date => (
														<CalendarCell
															className={clsx(
																styles["calendar__cell"],
																isToday(date.toString()) && styles["calendar__cell--today"],
															)}
															date={date}
														/>
													)}
												</CalendarGridBody>
											</CalendarGrid>
										</Calendar>
									</Dialog>
								</Popover>
							</FieldWrapper>
						</DatePicker>
					);
				}}
			/>
			{slotAfterInput}
		</I18nProvider>
	);
};
